import React from 'react';
import {
  Shield, Database, CheckCircle, BarChart3, Sparkles, DollarSign
} from 'lucide-react';
import { BATCH_PRICING } from '@/config/pricing';

export const BatchPricingTable: React.FC = () => {
  return (
    <div className="ca-card ca-card-p flex flex-col">
      <div className="mb-4">
        <h2 className="h2 mb-2">Transparent Pricing</h2>
        <p className="text-sm text-slate-600">
          Pay only for successfully processed records. No setup fees, no monthly minimums.
        </p>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto flex-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-900">Type</th>
              <th className="text-center py-2 px-4 text-xs font-semibold text-slate-900 border-l border-slate-200">5-24</th>
              <th className="text-center py-2 px-4 text-xs font-semibold text-slate-900 border-l border-slate-200">25-499</th>
              <th className="text-center py-2 px-4 text-xs font-semibold text-slate-900 border-l border-slate-200">500+</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr className="hover:bg-slate-50 transition-colors">
              <td className="py-2 px-3">
                <div className="flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-slate-900 text-xs">Validate</div>
                    <div className="text-xs text-slate-600">Identity verification</div>
                  </div>
                </div>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.validate.tier1.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.validate.tier2.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.validate.tier3.toFixed(2)}</span>
              </td>
            </tr>
            <tr className="hover:bg-slate-50 transition-colors">
              <td className="py-2 px-3">
                <div className="flex items-center gap-1.5">
                  <Database className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-slate-900 text-xs">Enrich</div>
                    <div className="text-xs text-slate-600">Contact enhancement</div>
                  </div>
                </div>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.enrich.tier1.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.enrich.tier2.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.enrich.tier3.toFixed(2)}</span>
              </td>
            </tr>
            <tr className="hover:bg-slate-50 transition-colors bg-blue-50/50">
              <td className="py-2 px-3">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-900 text-xs">Enrich + Validate</span>
                      <span className="text-xs font-medium text-blue-600 bg-blue-100 px-1 py-0.5 rounded-full">Popular</span>
                    </div>
                    <div className="text-xs text-slate-600">Complete verification</div>
                  </div>
                </div>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.enrich_validate.tier1.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.enrich_validate.tier2.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.enrich_validate.tier3.toFixed(2)}</span>
              </td>
            </tr>
            <tr className="hover:bg-slate-50 transition-colors">
              <td className="py-2 px-3">
                <div className="flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5 text-purple-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-slate-900 text-xs">Scoring</div>
                    <div className="text-xs text-slate-600">AI collectability</div>
                  </div>
                </div>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.scoring.tier1.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.scoring.tier2.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.scoring.tier3.toFixed(2)}</span>
              </td>
            </tr>
            <tr className="hover:bg-slate-50 transition-colors">
              <td className="py-2 px-3">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-purple-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-slate-900 text-xs">Scoring + Enrich</div>
                    <div className="text-xs text-slate-600">Maximum insights</div>
                  </div>
                </div>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.scoring_enrich.tier1.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.scoring_enrich.tier2.toFixed(2)}</span>
              </td>
              <td className="py-2 px-4 text-center border-l border-slate-200">
                <span className="text-xs text-slate-900">${BATCH_PRICING.scoring_enrich.tier3.toFixed(2)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3 flex-1">
        <div className="ca-card ca-card-p">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-slate-900">Validate</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.validate.tier1.toFixed(2)}</div>
              <div className="text-xs text-slate-500">5-24</div>
            </div>
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.validate.tier2.toFixed(2)}</div>
              <div className="text-xs text-slate-500">25-499</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-900">${BATCH_PRICING.validate.tier3.toFixed(2)}</div>
              <div className="text-xs text-slate-500">500+</div>
            </div>
          </div>
          <p className="text-sm text-slate-600">Identity verification and fraud detection</p>
        </div>

        <div className="ca-card ca-card-p">
          <div className="flex items-center gap-2 mb-2">
            <Database className="h-5 w-5 text-green-600" />
            <span className="font-medium text-slate-900">Enrich</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.enrich.tier1.toFixed(2)}</div>
              <div className="text-xs text-slate-500">5-24</div>
            </div>
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.enrich.tier2.toFixed(2)}</div>
              <div className="text-xs text-slate-500">25-499</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-900">${BATCH_PRICING.enrich.tier3.toFixed(2)}</div>
              <div className="text-xs text-slate-500">500+</div>
            </div>
          </div>
          <p className="text-sm text-slate-600">Contact enhancement and data completion</p>
        </div>

        <div className="ca-card ca-card-p bg-blue-50/50 border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-slate-900">Enrich + Validate</span>
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Popular</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.enrich_validate.tier1.toFixed(2)}</div>
              <div className="text-xs text-slate-500">5-24</div>
            </div>
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.enrich_validate.tier2.toFixed(2)}</div>
              <div className="text-xs text-slate-500">25-499</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-900">${BATCH_PRICING.enrich_validate.tier3.toFixed(2)}</div>
              <div className="text-xs text-slate-500">500+</div>
            </div>
          </div>
          <p className="text-sm text-slate-600">Contact enrichment with identity verification</p>
        </div>

        <div className="ca-card ca-card-p">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            <span className="font-medium text-slate-900">Scoring</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.scoring.tier1.toFixed(2)}</div>
              <div className="text-xs text-slate-500">5-24</div>
            </div>
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.scoring.tier2.toFixed(2)}</div>
              <div className="text-xs text-slate-500">25-499</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-900">${BATCH_PRICING.scoring.tier3.toFixed(2)}</div>
              <div className="text-xs text-slate-500">500+</div>
            </div>
          </div>
          <p className="text-sm text-slate-600">AI-powered collectability scoring</p>
        </div>

        <div className="ca-card ca-card-p">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <span className="font-medium text-slate-900">Scoring + Enrich</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.scoring_enrich.tier1.toFixed(2)}</div>
              <div className="text-xs text-slate-500">5-24</div>
            </div>
            <div className="text-center border-r border-slate-200 pr-2">
              <div className="text-xs text-slate-900">${BATCH_PRICING.scoring_enrich.tier2.toFixed(2)}</div>
              <div className="text-xs text-slate-500">25-499</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-900">${BATCH_PRICING.scoring_enrich.tier3.toFixed(2)}</div>
              <div className="text-xs text-slate-500">500+</div>
            </div>
          </div>
          <p className="text-sm text-slate-600">Complete scoring with data enrichment</p>
        </div>
      </div>

      {/* Pricing Notes */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="flex items-start gap-2 text-sm">
          <DollarSign className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-slate-600">
              Records that cannot be processed are never charged. AI Overview add-on available for Scoring types at +$0.03/record.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
